import { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"

const CATEGORIES = ["납품장비", "제품개발", "연구개발", "경영지원"]

export function ProjectManagement() {
  const [projects, setProjects] = useState<any[]>([])
  const [allProjects, setAllProjects] = useState<any[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingProject, setEditingProject] = useState<any>(null)
  const [filter, setFilter] = useState<"active" | "archived" | "all">("active")
  const [loading, setLoading] = useState(false)

  const [newProject, setNewProject] = useState({
    projectName: "",
    category: "납품장비",
    description: "",
    status: "active"
  })

  useEffect(() => {
    loadProjects()
  }, [filter])

  async function loadProjects() {
    setLoading(true)
    try {
      // 1단계 최적화: 프로젝트별 개별 조회(N+1) 제거
      // 전체 프로젝트 + 전체 투입시간을 각각 1회씩만 병렬 조회한 뒤,
      // projectId별로 totalHours를 메모리에서 합산한다.
      const [allResult, timeEntriesResult] = await Promise.all([
        lumi.entities.projects.list({
          filter: {},
          sort: { createdAt: -1 }
        }),
        lumi.entities.time_entries2.list({
          // totalHours 합산에 필요한 필드만 조회하여 전송량 감소
          projection: { projectId: 1, totalHours: 1 },
          limit: 5000
        })
      ])

      const allList = allResult.list || []
      setAllProjects(allList)

      // projectId -> 총 투입시간 맵 (단일 순회)
      const hoursByProject = (timeEntriesResult.list || []).reduce((acc, entry: any) => {
        const pid = entry.projectId
        if (!pid) return acc
        acc[pid] = (acc[pid] || 0) + (entry.totalHours || 0)
        return acc
      }, {} as Record<string, number>)

      // 현재 필터에 맞는 프로젝트만 추려 실제 투입시간 매핑
      const filteredList = filter === "all"
        ? allList
        : allList.filter((p: any) => p.status === filter)

      const projectsWithActualHours = filteredList.map((project: any) => ({
        ...project,
        actualTotalHours: hoursByProject[project._id] || 0
      }))

      setProjects(projectsWithActualHours)
    } catch (error) {
      console.error("프로젝트 로드 실패:", error)
    } finally {
      setLoading(false)
    }
  }

  async function createProject() {
    if (!newProject.projectName.trim()) {
      alert("프로젝트 이름을 입력하세요.")
      return
    }

    setLoading(true)
    try {
      await lumi.entities.projects.create({
        projectName: newProject.projectName,
        category: newProject.category,
        description: newProject.description,
        status: "active",
        totalHours: 0,
        creator: lumi.auth.user?.userId || "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      setNewProject({
        projectName: "",
        category: "납품장비",
        description: "",
        status: "active"
      })
      setShowCreateModal(false)
      loadProjects()
      alert("프로젝트가 생성되었습니다!")
    } catch (error) {
      console.error("프로젝트 생성 실패:", error)
      alert("프로젝트 생성에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  async function toggleArchive(projectId: string, currentStatus: string) {
    const newStatus = currentStatus === "active" ? "archived" : "active"
    const message = newStatus === "archived" 
      ? "이 프로젝트를 아카이브로 이동하시겠습니까?" 
      : "이 프로젝트를 활성 상태로 복원하시겠습니까?"

    if (!confirm(message)) return

    try {
      await lumi.entities.projects.update(projectId, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      })
      loadProjects()
      alert(newStatus === "archived" ? "아카이브로 이동했습니다." : "활성 상태로 복원했습니다.")
    } catch (error) {
      console.error("상태 변경 실패:", error)
      alert("상태 변경에 실패했습니다.")
    }
  }

  async function deleteProject(projectId: string) {
    if (!confirm("정말 이 프로젝트를 삭제하시겠습니까? 관련된 모든 시간 입력 데이터도 삭제됩니다.")) return

    try {
      await lumi.entities.projects.delete(projectId)
      loadProjects()
      alert("프로젝트가 삭제되었습니다.")
    } catch (error) {
      console.error("프로젝트 삭제 실패:", error)
      alert("프로젝트 삭제에 실패했습니다.")
    }
  }

  function openEditModal(project: any) {
    setEditingProject({
      _id: project._id,
      projectName: project.projectName,
      category: project.category,
      description: project.description || ""
    })
    setShowEditModal(true)
  }

  async function updateProject() {
    if (!editingProject || !editingProject.projectName.trim()) {
      alert("프로젝트 이름을 입력하세요.")
      return
    }

    setLoading(true)
    try {
      await lumi.entities.projects.update(editingProject._id, {
        projectName: editingProject.projectName,
        category: editingProject.category,
        description: editingProject.description,
        updatedAt: new Date().toISOString()
      })

      setShowEditModal(false)
      setEditingProject(null)
      loadProjects()
      alert("프로젝트가 수정되었습니다!")
    } catch (error) {
      console.error("프로젝트 수정 실패:", error)
      alert("프로젝트 수정에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }

  const activeProjects = allProjects.filter(p => p.status === "active")
  const archivedProjects = allProjects.filter(p => p.status === "archived")

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">프로젝트 관리</h2>
            <p className="text-sm sm:text-base text-gray-600">프로젝트를 생성하고 관리하세요</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            새 프로젝트
          </button>
        </div>
      </div>

      {/* 필터 탭 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setFilter("active")}
            className={`flex-1 py-3 px-4 font-medium transition-colors ${
              filter === "active"
                ? "text-orange-600 border-b-2 border-orange-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            활성 프로젝트 ({activeProjects.length})
          </button>
          <button
            onClick={() => setFilter("archived")}
            className={`flex-1 py-3 px-4 font-medium transition-colors ${
              filter === "archived"
                ? "text-orange-600 border-b-2 border-orange-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            아카이브 ({archivedProjects.length})
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`flex-1 py-3 px-4 font-medium transition-colors ${
              filter === "all"
                ? "text-orange-600 border-b-2 border-orange-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            전체 ({projects.length})
          </button>
        </div>
      </div>

      {/* 프로젝트 목록 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-600">프로젝트가 없습니다</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            첫 프로젝트 만들기
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {projects.map(project => (
            <div key={project._id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">{project.projectName}</h3>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    project.category === "납품장비" ? "bg-blue-100 text-blue-800" :
                    project.category === "제품개발" ? "bg-green-100 text-green-800" :
                    project.category === "연구개발" ? "bg-purple-100 text-purple-800" :
                    "bg-yellow-100 text-yellow-800"
                  }`}>
                    {project.category}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  project.status === "active" 
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}>
                  {project.status === "active" ? "활성" : "아카이브"}
                </span>
              </div>

              {project.description && (
                <p className="text-sm text-gray-600 mb-4">{project.description}</p>
              )}

              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <div>
                  <p className="text-xs text-gray-500">총 투입 시간</p>
                  <p className="text-lg font-semibold text-gray-900">{project.actualTotalHours?.toFixed(1) || 0}h</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">생성일</p>
                  <p className="text-sm text-gray-700">
                    {new Date(project.createdAt).toLocaleDateString("ko-KR")}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(project)}
                  className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  수정
                </button>
                <button
                  onClick={() => toggleArchive(project._id, project.status)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    project.status === "active"
                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                  }`}
                >
                  {project.status === "active" ? "아카이브" : "복원"}
                </button>
                <button
                  onClick={() => deleteProject(project._id)}
                  className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 프로젝트 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">새 프로젝트 만들기</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newProject.projectName}
                  onChange={(e) => setNewProject({ ...newProject, projectName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="프로젝트 이름을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  분류 <span className="text-red-500">*</span>
                </label>
                <select
                  value={newProject.category}
                  onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                  placeholder="프로젝트에 대한 설명을 입력하세요"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setNewProject({
                    projectName: "",
                    category: "납품장비",
                    description: "",
                    status: "active"
                  })
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={createProject}
                disabled={loading || !newProject.projectName.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "생성 중..." : "생성"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 프로젝트 수정 모달 */}
      {showEditModal && editingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">프로젝트 수정</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingProject.projectName}
                  onChange={(e) => setEditingProject({ ...editingProject, projectName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="프로젝트 이름을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  분류 <span className="text-red-500">*</span>
                </label>
                <select
                  value={editingProject.category}
                  onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                <textarea
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                  placeholder="프로젝트에 대한 설명을 입력하세요"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingProject(null)
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={updateProject}
                disabled={loading || !editingProject.projectName?.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "수정 중..." : "수정"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}